import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useScenarios } from '../hooks/useScenarios';
import { ScenarioForm } from '../components/ScenarioForm';
import { CreateScenarioDto } from '../types';
import { AdminPageHeader } from '../../admin/components/AdminPageHeader';
import { AssignLearnersModal } from '../../admin/components/AssignLearnersModal';
import { usePermissions } from '../../admin/hooks/usePermissions';
import { showToast, showError } from '../../../utils/alerts';

export const ScenarioCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { createScenario, publishScenario } = useScenarios({ autoFetch: false });
  const { canAssign } = usePermissions();
  const [submitting, setSubmitting] = useState(false);
  const [createdScenario, setCreatedScenario] = useState<{ id: string; title: string; tenantId?: string } | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  const handleSaveDraft = async (data: CreateScenarioDto) => {
    setSubmitting(true);
    try {
      const res = await createScenario(data);
      if (res.success && res.data) {
        showToast('Scenario saved as draft successfully!', 'success');
        navigate(`/scenarios/${res.data.id}`);
      } else {
        showError('Save Failed', (res.error as any)?.message || String(res.error || 'Failed to save scenario draft.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePublish = async (data: CreateScenarioDto) => {
    setSubmitting(true);
    try {
      const createRes = await createScenario(data);
      if (createRes.success && createRes.data) {
        const sim = createRes.data;
        setCreatedScenario({ id: sim.id, title: sim.title, tenantId: sim.tenantId });
        showToast('Scenario created! Assign learners to publish this scenario.', 'info');
        setIsAssignModalOpen(true);
      } else {
        showError('Creation Failed', (createRes.error as any)?.message || String(createRes.error || 'Failed to create scenario.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignCompleted = async (count: number) => {
    if (!createdScenario) return;
    setIsAssignModalOpen(false);
    try {
      const pubRes = await publishScenario(createdScenario.id);
      if (pubRes.success) {
        showToast(`Assigned to ${count} learners & published scenario successfully!`, 'success');
      } else {
        showToast(`Assigned to ${count} learners. (Draft mode preserved)`, 'info');
      }
      navigate(`/scenarios/${createdScenario.id}`);
    } catch {
      navigate(`/scenarios/${createdScenario.id}`);
    }
  };

  const handleModalClose = () => {
    setIsAssignModalOpen(false);
    if (createdScenario) {
      showToast('Scenario saved as Draft. Assign learners anytime to publish.', 'info');
      navigate(`/scenarios/${createdScenario.id}`);
    }
  };

  return (
    <div className="admin-page">
      <AdminPageHeader
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Scenarios', to: '/scenarios' },
          { label: 'Create Scenario' },
        ]}
        icon="✨"
        title="Create Simulation Scenario"
        badge="Case Studio"
        description="Author an AI simulation case with targeted personas, dialogue behaviors, multi-turn branches, and scoring rubrics."
        actions={
          <Link
            to="/scenarios"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              borderRadius: '8px',
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              color: '#FFFFFF',
              fontSize: '13px',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            ← Back to Scenarios
          </Link>
        }
      />

      <ScenarioForm
        isSubmitting={submitting}
        onSaveDraft={handleSaveDraft}
        onPublish={handlePublish}
        onCancel={() => navigate('/scenarios')}
      />

      {/* Assign Learners Modal before publish */}
      {canAssign && createdScenario && (
        <AssignLearnersModal
          simulationId={createdScenario.id}
          simulationTitle={createdScenario.title}
          tenantId={createdScenario.tenantId}
          isOpen={isAssignModalOpen}
          onClose={handleModalClose}
          onAssigned={handleAssignCompleted}
        />
      )}
    </div>
  );
};
